/**
 * AI Scribe V2 — socket payload normalisation & formatting helpers
 *
 * The audio socket is still the V1 contract (`{ start, transcription }`), so the
 * normaliser accepts that shape *and* the richer speaker/clinical-event shapes
 * without the UI having to care which one arrives.
 *
 * @author [Rajkumar Rathod]
 */

import { TranscriptionSegmentRequest } from "../../../../../../../../../services";
import { TranscriptSegment } from "../types";
import {
  CLINICAL_EVENT_CATEGORY_ALIASES,
  DEFAULT_SPEAKER_LABELS,
  diarisationSpeakerLabel,
  PATIENT_SPEAKER_HINTS,
  PRIMARY_DIARISATION_INDEX,
  PROVIDER_SPEAKER_HINTS,
} from "./constants";
import {
  ClinicalEvent,
  ClinicalEventCategory,
  ScribeSocketUpdate,
  ScribeSpeakerRole,
  ScribeTranscriptLine,
} from "./types";

/** One untyped frame node straight off the socket. */
type RawNode = Record<string, unknown>;

const asText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";

const asOptionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value : undefined;

const pad = (value: number) => String(value).padStart(2, "0");

/**
 * Elapsed session duration.
 * @returns `MM:SS` under an hour, `HH:MM:SS` beyond it.
 */
export const formatElapsed = (totalSeconds: number): string => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;

  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
};

// Bubbles show the socket's `start` verbatim, so no formatting is applied.
// /** Wall-clock label for a transcript bubble e.g. `5:18pm`. */
// const formatClockTime = (date: Date): string =>
//   date
//     .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
//     .replace(/\s/g, "")
//     .toLowerCase();

/** Matches bare diarisation labels: "0", "SPEAKER_01", "spk-2". */
const DIARISATION_LABEL = /^(?:speaker|spk)?[_ -]*(\d+)$/i;

/**
 * Read the diarisation index out of a bare speaker label.
 * @returns the index, or `undefined` when the label is a real name.
 */
const toDiarisationIndex = (rawSpeaker: unknown): number | undefined => {
  /** `speaker: 0` arrives as a number, so stringify before matching. */
  const label = String(rawSpeaker ?? "").trim();
  if (!label) return undefined;

  const digits = DIARISATION_LABEL.exec(label)?.[1];
  return digits === undefined ? undefined : Number(digits);
};

/** Is this the first voice, or everyone else? */
const isPrimarySpeaker = (index: number): boolean =>
  index === PRIMARY_DIARISATION_INDEX;

/** Resolve which lane a line belongs to from whatever the socket labelled it. */
const resolveSpeakerRole = (
  rawSpeaker: unknown,
  fallbackIndex: number
): ScribeSpeakerRole => {
  const speaker = String(rawSpeaker ?? "")
    .trim()
    .toLowerCase();

  if (!speaker) return "UNKNOWN";
  if (PROVIDER_SPEAKER_HINTS.some((hint) => speaker.includes(hint)))
    return "PROVIDER";
  if (PATIENT_SPEAKER_HINTS.some((hint) => speaker.includes(hint)))
    return "PATIENT";

  /**
   * Diarisation labels ("0", "SPEAKER_01", "spk_1") carry no semantics — the
   * first index takes the left lane and every other index the right one, so a
   * recogniser that drifts to `speaker: 2` does not jump back across the panel.
   */
  const diarisationIndex = toDiarisationIndex(speaker);
  if (diarisationIndex !== undefined)
    return isPrimarySpeaker(diarisationIndex) ? "PROVIDER" : "PATIENT";

  return fallbackIndex % 2 === 0 ? "PROVIDER" : "PATIENT";
};

/** Map a backend category string onto one of our card styles. */
const resolveEventCategory = (raw: unknown): ClinicalEventCategory => {
  const key = String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

  return CLINICAL_EVENT_CATEGORY_ALIASES[key] ?? "OTHER";
};

interface NormaliseContext {
  /** Wall-clock time the session started, used to turn offsets into clock labels */
  sessionStartedAt: number;
  /** Running count of lines already rendered — seeds speaker alternation */
  lineOffset: number;
  /** Names to show on the bubbles */
  providerName?: string;
  patientName?: string;
}

const EMPTY_UPDATE: ScribeSocketUpdate = { lines: [], events: [] };

/**
 * Frames arrive as JSON strings, but a plain-text socket is just as valid a
 * contract — an unparseable string is treated as the utterance itself rather
 * than thrown away.
 */
const safeParse = (raw: unknown): unknown => {
  if (typeof raw !== "string") return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

/**
 * A container key only counts when it actually holds node(s).
 * `{ transcript: "hello" }` carries the text *in* that key — reading it as a
 * list of segments yields nothing and silently drops the utterance.
 */
const asNodeContainer = (value: unknown): RawNode[] | undefined =>
  Array.isArray(value) || (!!value && typeof value === "object")
    ? asArray(value)
    : undefined;

const toTranscriptLine = (
  node: RawNode,
  index: number,
  ctx: NormaliseContext
): ScribeTranscriptLine | null => {
  /** Whatever the backend sends is rendered verbatim; only blanks are skipped. */
  const text = asText(
    node.transcription ?? node.transcript ?? node.text ?? node.content
  ).trim();
  if (!text) return null;

  const absoluteIndex = ctx.lineOffset + index;

  const rawStart = node.start ?? node.startTime ?? node.offset;
  const start =
    typeof rawStart === "number" || typeof rawStart === "string"
      ? rawStart
      : undefined;

  /** `??` and not `||`, so a numeric `speaker: 0` is not skipped as falsy. */
  const rawSpeaker =
    node.role ?? node.speakerRole ?? node.speaker ?? node.speakerId;

  const role = resolveSpeakerRole(rawSpeaker, absoluteIndex);

  const explicitName =
    asOptionalString(node.speakerName) ?? asOptionalString(node.displayName);
  const speakerLabel = asOptionalString(node.speaker)?.trim();
  /** Ignore diarisation placeholders like "SPEAKER_01" — they aren't names. */
  const namedSpeaker =
    speakerLabel && !DIARISATION_LABEL.test(speakerLabel)
      ? speakerLabel
      : undefined;

  /**
   * An index is all the socket knows, so the bubble says exactly that rather
   * than asserting who is the provider and who is the patient. The label
   * mirrors the socket's index verbatim — "Speaker 3", "Speaker 4", … — instead
   * of folding every voice past the first into one.
   */
  const diarisationIndex = toDiarisationIndex(rawSpeaker);
  const diarisationName =
    diarisationIndex === undefined
      ? undefined
      : diarisationSpeakerLabel(diarisationIndex);

  /** Only reached when the socket said nothing identifying at all. */
  const fallbackName =
    role === "PATIENT"
      ? (ctx.patientName ?? DEFAULT_SPEAKER_LABELS.PATIENT)
      : (ctx.providerName ?? DEFAULT_SPEAKER_LABELS.PROVIDER);

  return {
    id: `line-${absoluteIndex}-${start ?? absoluteIndex}`,
    speaker: explicitName ?? namedSpeaker ?? diarisationName ?? fallbackName,
    role,
    text,
    timestamp: start ? String(start) : "00:00",
    start,
  };
};

const toClinicalEvent = (
  node: RawNode,
  index: number,
  ctx: NormaliseContext
): ClinicalEvent | null => {
  const summary = asText(
    node.summary ?? node.text ?? node.description ?? node.value
  ).trim();
  if (!summary) return null;

  return {
    id:
      asOptionalString(node.id) ??
      `event-${ctx.lineOffset}-${index}-${summary.slice(0, 24)}`,
    category: resolveEventCategory(
      node.category ?? node.type ?? node.eventType
    ),
    summary,
    detail: asOptionalString(node.detail),
  };
};

const asArray = (value: unknown): RawNode[] => {
  if (Array.isArray(value))
    return value.filter(
      (node): node is RawNode => !!node && typeof node === "object"
    );
  if (value && typeof value === "object") return [value as RawNode];
  return [];
};

/**
 * Turn one raw websocket frame into transcript lines + clinical events.
 *
 * Accepted shapes:
 * - V1 audio socket: `{ start, transcription }`
 * - Envelope:        `{ type: "TRANSCRIPT" | "CLINICAL_EVENT", data: {...} | [...] }`
 * - Combined:        `{ transcript: [...], clinicalEvents: [...] }`
 * - Arrays of any of the above
 */
export const normaliseScribeFrame = (
  raw: unknown,
  ctx: NormaliseContext
): ScribeSocketUpdate => {
  const parsed = safeParse(raw);

  /** A bare string frame is one utterance. */
  if (typeof parsed === "string") {
    const line = toTranscriptLine({ text: parsed }, 0, ctx);
    return line ? { lines: [line], events: [] } : EMPTY_UPDATE;
  }

  if (!parsed || typeof parsed !== "object") return EMPTY_UPDATE;

  if (Array.isArray(parsed)) {
    return parsed.reduce<ScribeSocketUpdate>(
      (acc, node) => {
        const next = normaliseScribeFrame(node, {
          ...ctx,
          lineOffset: ctx.lineOffset + acc.lines.length,
        });
        return {
          lines: [...acc.lines, ...next.lines],
          events: [...acc.events, ...next.events],
        };
      },
      { lines: [], events: [] }
    );
  }

  const node = parsed as RawNode;
  const envelopeType = asText(node.type).toUpperCase();

  /** Explicit clinical-event frames. */
  if (envelopeType.includes("CLINICAL") || envelopeType === "EVENT") {
    return {
      lines: [],
      events: asArray(node.data ?? node.events ?? node)
        .map((n, i) => toClinicalEvent(n, i, ctx))
        .filter(Boolean) as ClinicalEvent[],
    };
  }

  /**
   * Recognisers that wrap the result in a channel expose the text one level
   * down (`{ channel: { alternatives: [{ transcript }] } }`).
   */
  const channel = node.channel as RawNode | undefined;
  const alternatives = channel && asNodeContainer(channel.alternatives);

  const transcriptNodes = alternatives ??
    asNodeContainer(node.transcript) ??
    asNodeContainer(node.transcriptions) ??
    asNodeContainer(node.segments) ??
    asNodeContainer(node.data) ?? [node];

  const eventNodes = asArray(node.clinicalEvents ?? node.events);

  return {
    lines: transcriptNodes
      .map((n, i) => toTranscriptLine(n, i, ctx))
      .filter(Boolean) as ScribeTranscriptLine[],
    events: eventNodes
      .map((n, i) => toClinicalEvent(n, i, ctx))
      .filter(Boolean) as ClinicalEvent[],
  };
};

/**
 * Shape the transcript back into the payload `generateNoteFromSegments` expects.
 * Keeps the V1 API contract intact so the note-generation endpoint is unchanged.
 */
export const toTranscriptionSegments = (
  lines: ScribeTranscriptLine[]
): TranscriptionSegmentRequest[] =>
  lines.map((line) => ({
    start: line.start === undefined ? undefined : String(line.start),
    transcription: line.text,
  }));

/** Shape the transcript for the V1 `TranscriptionSection` reader view. */
export const toDisplaySegments = (
  lines: ScribeTranscriptLine[]
): TranscriptSegment[] =>
  lines.map((line) => ({
    start: Number(line.start ?? 0) || 0,
    speaker: line.speaker,
    timestamp: line.timestamp,
    end: 0,
    text: line.text,
  }));
